import { useMemo } from 'react';
import type { DataTableSort } from './types.ts';

function rank(value: unknown): number {
  return value === null || value === undefined || value === '' ? 1 : 0;
}

function compare(a: unknown, b: unknown): number {
  /* Missing values sort last in both directions: absence is not a low score. */
  const emptiness = rank(a) - rank(b);
  if (emptiness !== 0 || rank(a) === 1) {
    return emptiness;
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }
  if (typeof a === 'boolean' && typeof b === 'boolean') {
    return Number(a) - Number(b);
  }
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() - b.getTime();
  }

  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

/**
 * Sorting for a table whose rows are already all here.
 *
 * The direction is applied after the comparison rather than inside it, so the
 * "missing last" rule holds whichever way the column is pointing.
 */
export function useClientSort<T>(rows: T[], sort?: DataTableSort): T[] {
  const key = sort?.key;
  const direction = sort?.direction;

  return useMemo(() => {
    if (key === undefined) {
      return rows;
    }
    const read = (row: T) => (row as unknown as Record<string, unknown>)[key];

    return [...rows].sort((a, b) => {
      const emptiness = rank(read(a)) - rank(read(b));

      return emptiness !== 0
        ? emptiness
        : (direction === 'desc' ? -1 : 1) * compare(read(a), read(b));
    });
  }, [rows, key, direction]);
}
